// -----------------------------------------------------------
// FloraIQ - Nuvem de Pontos e Modelo 3D (Página Principal)
// -----------------------------------------------------------
// Unica fonte real hoje: GET /point-cloud/tree-metrics (gis.v_tree_metrics_3d).
// Levantamentos, ativos, classificacao, interferencias 3D, comparacao temporal
// e relatorios nao tem cliente no frontend ainda -> NoRealData.

import { useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import KpiCard from "@/components/shared/KpiCard";
import { NoRealData } from "@/components/shared";
import { useTreeMetrics3DList } from "@/hooks";
import TreeMetrics3DTable from "./components/TreeMetrics3DTable";

type TabId = "metrics" | "surveys" | "viewer" | "classification" | "interferences" | "temporal" | "quality" | "reports";

const tabs: { id: TabId; label: string; icon: string }[] = [
  { id: "metrics", label: "Métricas por Exemplar", icon: "ri-plant-line" },
  { id: "surveys", label: "Levantamentos", icon: "ri-flight-takeoff-line" },
  { id: "viewer", label: "Visualizador 3D", icon: "ri-stack-line" },
  { id: "classification", label: "Classificação", icon: "ri-palette-line" },
  { id: "interferences", label: "Interferências 3D", icon: "ri-error-warning-line" },
  { id: "temporal", label: "Comparação Temporal", icon: "ri-contrast-2-line" },
  { id: "quality", label: "Qualidade", icon: "ri-check-double-line" },
  { id: "reports", label: "Relatórios", icon: "ri-file-chart-line" },
];

const placeholders: Record<Exclude<TabId, "metrics">, { title: string; icon: string; description: string }> = {
  surveys: {
    title: "Levantamentos e ativos 3D",
    icon: "ri-flight-takeoff-line",
    description: "O backend expõe GET /point-cloud/assets, mas a tela de levantamentos ainda não foi ligada. Importação de LAS/LAZ e ortofotos não está implementada.",
  },
  viewer: {
    title: "Visualizador 3D",
    icon: "ri-stack-line",
    description: "Não há visualizador de nuvem de pontos integrado (Potree, Cesium ou similar).",
  },
  classification: {
    title: "Classificação da nuvem",
    icon: "ri-palette-line",
    description: "Não há processamento de classificação de pontos registrado no banco.",
  },
  interferences: {
    title: "Interferências 3D",
    icon: "ri-error-warning-line",
    description: "Interferências detectadas a partir da nuvem de pontos ainda não são calculadas nem gravadas.",
  },
  temporal: {
    title: "Comparação temporal",
    icon: "ri-contrast-2-line",
    description: "Comparação entre levantamentos depende de mais de um levantamento processado por área, o que ainda não existe.",
  },
  quality: {
    title: "Qualidade dos ativos 3D",
    icon: "ri-check-double-line",
    description: "Pendências de metadados das nuvens aparecem em Qualidade dos Dados > Nuvem de Pontos (regra POINT_CLOUD_METADATA_REQUIRED).",
  },
  reports: {
    title: "Relatórios 3D",
    icon: "ri-file-chart-line",
    description: "A geração de relatórios 3D ainda não foi implementada.",
  },
};

export default function PointCloudPage() {
  const [activeTab, setActiveTab] = useState<TabId>("metrics");
  const metrics = useTreeMetrics3DList();

  const kpi = (n: number) => (metrics.loading ? "…" : metrics.error ? "—" : n.toLocaleString("pt-BR"));
  const rows = metrics.items;
  const com3d = rows.filter((r) => r.altura3D !== null).length;
  const validados = rows.filter((r) => r.status === "validado").length;
  const divergentes = rows.filter((r) => r.status === "divergente").length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Nuvem de Pontos e Modelo 3D"
        subtitle="Métricas 3D extraídas de levantamentos e comparadas com a medição de campo dos exemplares arbóreos do CTA."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <KpiCard label="Exemplares na comparação" value={kpi(rows.length)} icon="ri-plant-line" variant="default" />
        <KpiCard label="Com altura 3D" value={kpi(com3d)} icon="ri-ruler-line" variant="info" />
        <KpiCard label="Validados campo x 3D" value={kpi(validados)} icon="ri-checkbox-circle-line" variant="success" />
        <KpiCard label="Divergentes campo x 3D" value={kpi(divergentes)} icon="ri-contrast-2-line" variant="warning" />
      </div>

      <div className="bg-white rounded-card border border-background-200/70 overflow-hidden">
        <div className="border-b border-background-200/50 overflow-x-auto scrollbar-thin">
          <div className="flex px-1 pt-1 gap-0.5 min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium whitespace-nowrap transition-all duration-200 cursor-pointer rounded-t-md border-b-2 -mb-[1px] ${
                  activeTab === tab.id
                    ? "text-blue-600 border-blue-500 bg-blue-50/50"
                    : "text-foreground-500 border-transparent hover:text-foreground-700 hover:bg-background-50/50"
                }`}
              >
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-5 fade-in" key={activeTab}>
          {activeTab === "metrics" ? (
            <TreeMetrics3DTable items={rows} loading={metrics.loading} error={metrics.error} />
          ) : (
            <NoRealData {...placeholders[activeTab]} />
          )}
        </div>
      </div>
    </div>
  );
}
